"use client"

/**
 * NativeSelectAdapter (docs/THEME.md 3.6; std-overlays foundation): the ONLY way a native `<select>` converts to the
 * shared dropdown. It keeps native-select semantics, so a conversion is a tag swap:
 *
 *   - <select value={v} onChange={(e) => setV(e.target.value)} name="unit" required>
 *   -   <option value="">All units</option>
 *   -   {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
 *   - </select>
 *   + <NativeSelectAdapter value={v} onChange={(e) => setV(e.target.value)} name="unit" required aria-label="Unit">
 *   +   ...the same <option>/<optgroup> children...
 *   + </NativeSelectAdapter>
 *
 * Kept exactly: the values (an empty "" option round-trips as "": it is rendered with a reserved item value and mapped
 * back, never as `SelectItem value=""`), `onChange(event)` with `event.target.value` / `event.target.name`
 * (a synthetic event: `NativeSelectChangeEvent`; a handler typed `React.ChangeEvent<HTMLSelectElement>` must be
 * re-typed, never cast), `onValueChange(value)` for string callers, `name` / `form` submission (the submitted value
 * is the real value, "" included), `required` (missing = the same rule as native), form reset (back to the default),
 * `disabled`, disabled and hidden options, optgroups. An uncontrolled adapter with no `defaultValue` starts on the
 * first enabled option, as a native select does. Invalid submit moves focus to the trigger.
 * NOT supported (stay native, a separate pattern): `multiple` and the numeric `size` (listbox rows).
 */
import * as React from "react"

import { SimpleSelect, type SelectOption, type SelectOptionGroup, type SelectOptions, type SimpleSelectProps, flattenOptions } from "./simple-select"

/** What `onChange` receives: the subset of a native change event that select handlers read. */
export interface NativeSelectChangeEvent {
  readonly type: "change"
  readonly target: { readonly name: string; readonly value: string; readonly id: string }
  readonly currentTarget: { readonly name: string; readonly value: string; readonly id: string }
  preventDefault(): void
  stopPropagation(): void
  persist(): void
  isDefaultPrevented(): boolean
}

export interface NativeSelectAdapterProps extends Omit<SimpleSelectProps, "options" | "value" | "defaultValue"> {
  /** `<option>` / `<optgroup>` children, exactly as they were inside the native select. */
  children?: React.ReactNode
  /** Or the options as data (then children are ignored). */
  options?: SelectOptions
  value?: string | number
  defaultValue?: string | number
  onChange?: (event: NativeSelectChangeEvent) => void
  /** Native-only props that need a different pattern. Passing them is a type error. */
  multiple?: never
}

function textOf(node: React.ReactNode): string {
  let out = ""
  React.Children.forEach(node, (child) => {
    if (typeof child === "string" || typeof child === "number") out += String(child)
    else if (React.isValidElement<{ children?: React.ReactNode }>(child)) out += textOf(child.props.children)
  })
  return out
}

interface OptionProps {
  value?: string | number
  label?: string
  disabled?: boolean
  hidden?: boolean
  children?: React.ReactNode
}

function optionFrom(props: OptionProps): SelectOption {
  const text = textOf(props.children)
  return {
    value: props.value !== undefined ? String(props.value) : text,
    label: props.label ?? (text || props.children),
    textValue: props.label ?? text,
    disabled: props.disabled,
    hidden: props.hidden,
  }
}

/** The options of `<option>` / `<optgroup>` children (fragments and arrays flattened). Exported for tests. */
export function optionsFromChildren(children: React.ReactNode): Array<SelectOption | SelectOptionGroup> {
  const out: Array<SelectOption | SelectOptionGroup> = []
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement<{ children?: React.ReactNode; label?: string }>(child)) return
    if (child.type === React.Fragment) out.push(...optionsFromChildren(child.props.children))
    else if (child.type === "option") out.push(optionFrom(child.props as OptionProps))
    else if (child.type === "optgroup")
      out.push({ label: child.props.label ?? "", options: flattenOptions(optionsFromChildren(child.props.children)) })
    else if (process.env.NODE_ENV !== "production") throw new Error("NativeSelectAdapter: children must be <option> or <optgroup>")
  })
  return out
}

export const NativeSelectAdapter = React.forwardRef<HTMLButtonElement, NativeSelectAdapterProps>(function NativeSelectAdapter(
  { children, options: optionsProp, value, defaultValue, onChange, onValueChange, name, id, ...rest },
  ref,
) {
  const options = React.useMemo(() => optionsProp ?? optionsFromChildren(children), [optionsProp, children])
  // A native select with no value starts on its first enabled option.
  const initial = React.useMemo(() => {
    if (defaultValue !== undefined) return String(defaultValue)
    return flattenOptions(options).find((o) => !o.disabled)?.value ?? ""
  }, [defaultValue, options])

  const handle = (next: string) => {
    onValueChange?.(next)
    if (onChange) {
      const target = { name: name ?? "", value: next, id: id ?? "" }
      let prevented = false
      onChange({
        type: "change",
        target,
        currentTarget: target,
        preventDefault: () => { prevented = true },
        stopPropagation: () => {},
        persist: () => {},
        isDefaultPrevented: () => prevented,
      })
    }
  }

  return (
    <SimpleSelect
      ref={ref}
      {...rest}
      id={id}
      name={name}
      options={options}
      value={value === undefined ? undefined : String(value)}
      defaultValue={value === undefined ? initial : undefined}
      onValueChange={handle}
    />
  )
})
